import { Store, EventBus, DataRouter } from "@xbs/lib-state";
import { getViewById, registerViews } from "./registeredViews";
import { defaultEditorShape, defaultCalendars, defaultColors } from "./consts";
import { normalizeEditor, normalizeConfig, uid, normalizeCalendars, isDeepEqual, extractNotEqualKeys, } from "./helpers";
import { getEventsByBounds, normalizeEvent } from "./events";
import { isAfter, isWithinInterval } from "date-fns";
import { checkIntegrity } from "./package/package";
import { cleanEventFromRecurring, getRecurringById } from "./recurring";
import { isSameTime } from "./time";
export default class DataStore extends Store {
    constructor(w) {
        super(w);
        this._uniqueId = new Set();
        checkIntegrity();
        this._router = new DataRouter(super.setState.bind(this), [
            {
                in: ["mode", "date"],
                out: ["bounds"],
                exec: (ctx) => {
                    const { mode, date, config, bounds: prevBounds, } = this.getState();
                    const view = this._registeredViews[mode];
                    if (!view)
                        return;
                    const bounds = view.getBounds(date, getViewById(config, mode)?.config || {});
                    if (!isDeepEqual(bounds, prevBounds)) {
                        this.setState({ bounds }, ctx);
                    }
                },
            },
            {
                in: ["events", "bounds", "calendars"],
                out: ["viewData"],
                exec: (ctx) => {
                    const { events, bounds, calendars, dateFnsLocale } = this.getState();
                    const disabledCalendars = calendars.filter(i => !i.active);
                    let viewData = [];
                    if (bounds) {
                        const [start, end] = bounds;
                        const filteredEvents = events.filter(({ start_date, end_date, DTEND, originalStartTime, type, }) => {
                            const isDisabled = disabledCalendars.find(c => c.id === type);
                            if (isDisabled)
                                return false;
                            if (!originalStartTime &&
                                (!start_date || !end_date))
                                return false;
                            let excludedInRange = false;
                            if (originalStartTime) {
                                excludedInRange = isWithinInterval(originalStartTime, { start, end });
                            }
                            const isInRange = start_date < end &&
                                (DTEND > start || end_date > start);
                            return (isInRange ||
                                (!isInRange && excludedInRange));
                        });
                        viewData = getEventsByBounds(filteredEvents, bounds, disabledCalendars, dateFnsLocale);
                    }
                    this.setState({ viewData }, ctx);
                },
            },
            {
                in: ["mode", "bounds", "viewData", "calendars"],
                out: ["viewModel"],
                exec: (ctx) => {
                    const { date, bounds, mode, viewData, config, calendars, } = this.getState();
                    const view = this._registeredViews[mode];
                    // TODO: can we do it only when we have a mode?
                    if (view) {
                        const viewConfig = getViewById(config, mode)?.config || {};
                        const viewModel = view.getViewModel(date, bounds, {
                            dateTitle: config.dateTitle,
                            timeStep: config.timeStep,
                            timeRange: config.timeRange,
                            eventsOverlay: config.eventsOverlay,
                            ...viewConfig,
                            autoSave: config.autoSave,
                        }, viewData, calendars
                            .slice()
                            .sort((a, b) => +b.active - +a.active));
                        this.setState({ viewModel }, ctx);
                    }
                },
            },
            {
                in: ["mode", "bounds"],
                out: ["datepicker"],
                exec: (ctx) => {
                    const { date, bounds } = this.getState();
                    const markers = (day) => {
                        return isWithinInterval(day, {
                            start: bounds[0],
                            end: bounds[1],
                        })
                            ? "inrange"
                            : "";
                    };
                    const datepicker = {
                        current: date,
                        markers,
                    };
                    this.setState({ datepicker }, ctx);
                },
            },
        ], {});
        const inBus = (this.in = new EventBus());
        inBus.on("set-mode", ({ value }) => {
            this.setState({ mode: value });
        });
        inBus.on("set-date", ({ value }) => {
            this.setState({ date: new Date(value) });
        });
        inBus.on("update-event", ({ event, id, mode }) => {
            const { events, calendars, config } = this.getState();
            if (!this._uniqueId.has(id))
                return;
            const prevEvent = events.find(i => i.id === id);
            const newEvent = { ...prevEvent, ...event };
            const filtered = events.filter(i => i !== prevEvent);
            this.setState({
                events: [
                    ...filtered,
                    normalizeEvent({ event: newEvent, calendars, config }),
                ],
            });
            const { recurring, recurringEventId, start_date, end_date } = newEvent;
            if (!recurring && !recurringEventId)
                return;
            const parent_id = recurringEventId || id;
            const children = filtered.filter(ev => ev.recurringEventId === parent_id);
            // seria was splitted or updated with UNTIL or COUNT (instead of future mode)
            if (prevEvent.DTEND?.valueOf() !== newEvent.DTEND?.valueOf()) {
                children
                    .filter(ev => isAfter(ev.start_date, newEvent.DTEND))
                    .forEach(ev => {
                    inBus.exec("delete-event", { id: ev.id });
                });
            }
            // probably we can understand that its all mode with another way
            if (mode === "all") {
                if (!isSameTime(prevEvent.start_date, start_date) ||
                    !isSameTime(prevEvent.end_date, end_date)) {
                    children.forEach(ev => {
                        inBus.exec("delete-event", { id: ev.id });
                    });
                    return;
                }
                children.forEach(ev => {
                    const { id, start_date, end_date, ...rest } = event;
                    inBus.exec("update-event", {
                        id: ev.id,
                        event: { ...ev, ...cleanEventFromRecurring(rest) },
                    });
                });
            }
        });
        inBus.on("update-calendar", ({ calendar, id }) => {
            const { calendars, editorShape } = this.getState();
            const item = calendars.filter(i => i.id === id)[0];
            if (!item)
                return;
            copyObjectData(calendar, item);
            const typeCombo = editorShape.find(i => i.key === "type");
            if (typeCombo) {
                const option = typeCombo.options.find(o => o.id === id);
                if (option) {
                    copyObjectData(calendar, option);
                }
            }
            this.setState({
                calendars,
                editorShape,
            });
        });
        inBus.on("add-calendar", ({ calendar }) => {
            const { calendars, colors, editorShape } = this.getState();
            const data = {
                id: uid(),
                ...calendar,
            };
            const typeCombo = editorShape.find(i => i.key === "type");
            if (typeCombo) {
                typeCombo.options = [
                    ...typeCombo.options.filter(i => i.id !== data.id),
                    data,
                ];
            }
            this.setState({
                calendars: normalizeCalendars([...calendars, data], colors),
                editorShape,
            });
        });
        inBus.on("delete-calendar", ({ id }) => {
            const { calendars, events, editorShape } = this.getState();
            const typeCombo = editorShape.find(i => i.key === "type");
            if (typeCombo) {
                typeCombo.options = typeCombo.options.filter(o => o.id !== id);
            }
            this.setState({
                calendars: calendars.filter(c => c.id !== id),
                events: events.filter((ev) => ev.type !== id),
                editorShape,
            });
        });
        inBus.on("add-event", ({ event }) => {
            const { events, calendars, config } = this.getState();
            if (!calendars.length) {
                inBus.exec("add-calendar", {
                    calendar: { label: "New Calendar" },
                });
            }
            const newEvent = normalizeEvent({ event, calendars, config });
            this._checkId(newEvent.id);
            this.setState({
                events: [...events, newEvent],
            });
        });
        inBus.on("edit-event", (config) => {
            if (config) {
                let { selected } = this.getState();
                const { events, calendars, config: { defaultEditorValues = ev => ev }, } = this.getState();
                const { id, add } = config;
                if (add) {
                    selected = defaultEditorValues(typeof add === "boolean" ? {} : add);
                    if (!calendars.length) {
                        inBus.exec("add-calendar", {
                            calendar: { label: "New Calendar" },
                        });
                    }
                }
                else {
                    if (`${id}`.includes("wx_recurring")) {
                        selected = getRecurringById(id, events);
                    }
                    else {
                        selected =
                            events.find(item => item.id === id) || selected;
                    }
                }
                this.setState({
                    selected,
                    edit: add ? "add" : selected?.readonly ? false : "update",
                    popupInfo: null,
                });
            }
            else {
                const date = this.getState().date;
                this.setState({ edit: false, date });
            }
        });
        inBus.on("close-event-info", () => {
            this.setState({ popupInfo: null });
        });
        inBus.on(
        // probably should change to set 'set-next-interval' 'set-prev-interval' or something
        // since we won't have step different from 1 and -1
        "set-bound", ({ step }) => {
            const { mode, date, config } = this.getState();
            const view = this._registeredViews[mode];
            const nextDate = step > 0
                ? view.getNext(date, getViewById(config, mode)?.config || {})
                : view.getPrev(date, getViewById(config, mode)?.config || {});
            this.setState({ date: nextDate });
        });
        inBus.on("select-event", ({ id, popup }) => {
            const { events, viewData, selectedId } = this.getState();
            let recurringEventId;
            if (selectedId === id)
                return this.setState({ popupInfo: !!popup });
            if (`${id}`.includes("wx_recurring")) {
                recurringEventId = `${id}`.split("wx_recurring")[1];
            }
            let event = events.find(ev => ev.id == (recurringEventId || id));
            if (recurringEventId) {
                event = getRecurringById(id, events);
            }
            if (!event)
                return;
            const isHidden = !viewData.find(ev => ev.id === event.id);
            if (isHidden) {
                this.setState({
                    date: new Date(event.start_date),
                });
            }
            this.setState({
                selectedId: id,
                selected: event,
                popupInfo: !!popup,
            });
        });
        inBus.on("toggle-sidebar", (data) => {
            const { sidebar } = this.getState();
            if (!sidebar)
                return;
            this.setState({
                sidebar: data ? data : { show: !sidebar.show },
            });
        });
        inBus.on("delete-event", ({ id }) => {
            const { events } = this.getState();
            if (!this._uniqueId.has(id))
                return;
            let eventToDelete;
            const filtered = events.filter(ev => {
                if (ev.id !== id)
                    return true;
                eventToDelete = ev;
            });
            this._uniqueId.delete(id);
            this.setState({ events: filtered, edit: false });
            if (eventToDelete.recurring) {
                events
                    .filter(ev => ev.recurringEventId === id)
                    .forEach(ev => {
                    inBus.exec("delete-event", { id: ev.id });
                });
            }
        });
    }
    init(state) {
        const { colors, config, ...rest } = state;
        const prevState = this.getState();
        const cnf = normalizeConfig(config, prevState.config);
        const initData = {
            selected: null,
            selectedId: null,
            edit: false,
            popupInfo: null,
            events: [],
            editorShape: defaultEditorShape,
            bounds: [new Date(), new Date()],
            sidebar: { show: true },
            colors: colors?.length ? colors : defaultColors,
            datepicker: {
                current: new Date(),
                markers: null,
            },
            ...prevState,
            ...rest,
            config: cnf,
        };
        this._registeredViews = {};
        const data = { ...initData, ...this.normalizeState(initData) };
        Object.keys(prevState).length
            ? this.setState(extractNotEqualKeys(prevState, data))
            : this._router.init(data);
    }
    setState(state, ctx) {
        this._router.setState(state, ctx);
    }
    normalizeState(initData) {
        this._uniqueId.clear();
        const { config, colors } = initData;
        let { events = [], calendars = [], selectedId, edit, selected, popupInfo, } = initData;
        this._registeredViews = registerViews(config.views);
        calendars = calendars.length
            ? normalizeCalendars(calendars, colors)
            : defaultCalendars;
        let resetSelected = false;
        events =
            events.length && calendars.length
                ? events.map(e => {
                    const event = normalizeEvent({
                        event: e,
                        calendars,
                        config,
                    });
                    this._checkId(event.id);
                    if (selectedId === event.id) {
                        selectedId = event.id;
                        selected = event;
                        resetSelected = true;
                    }
                    return event;
                })
                : [];
        const editorShape = normalizeEditor({
            ...initData,
            events,
            calendars,
            config,
        });
        if (!resetSelected) {
            selected = null;
            selectedId = null;
            edit = edit === "update" ? false : edit;
            popupInfo = null;
        }
        return {
            editorShape,
            events,
            calendars,
            selectedId,
            edit,
            selected,
            popupInfo,
        };
    }
    _checkId(id) {
        if (this._uniqueId.has(id)) {
            throw new Error(`You have duplicated id: ${id}`);
        }
        this._uniqueId.add(id);
    }
}
function copyObjectData(source, target) {
    Object.keys(source).forEach(key => {
        if (key !== "id") {
            target[key] = source[key];
        }
    });
}
//# sourceMappingURL=DataStore.js.map